import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import BrandMark from "@/components/BrandMark";
import { useLocale } from "@/lib/locale";
import { ArrowRight, Image, MessageSquare, MessageCircle } from "lucide-react";

/**
 * Simple onboarding screen for first-time users.
 * Shows right after signup (or when the user has zero presences yet).
 *
 * Three clear visual steps — all guided, no poetry, nothing to figure out:
 *   1. Add a photo
 *   2. Write a memory
 *   3. Start speaking
 */
export default function Welcome() {
    const nav = useNavigate();
    const { t } = useLocale();
    const [checking, setChecking] = useState(true);

    // If the user already has presences, skip onboarding — they've been here before.
    useEffect(() => {
        let cancelled = false;
        api.get("/presences")
            .then(({ data }) => {
                if (cancelled) return;
                if (Array.isArray(data) && data.length > 0) {
                    nav("/dashboard", { replace: true });
                } else {
                    setChecking(false);
                }
            })
            .catch(() => setChecking(false));
        return () => { cancelled = true; };
    }, [nav]);

    if (checking) {
        return (
            <div className="grain min-h-screen candle-halo">
                <Header minimal />
            </div>
        );
    }

    const steps = [
        { n: "01", icon: Image,          label: t("welcome.step1") },
        { n: "02", icon: MessageSquare,  label: t("welcome.step2") },
        { n: "03", icon: MessageCircle,  label: t("welcome.step3") },
    ];

    return (
        <div className="grain min-h-screen candle-halo" data-testid="welcome-page">
            <Header minimal />
            <main className="max-w-3xl mx-auto px-6 md:px-12 pt-16 pb-24 animate-fade-in">
                <div className="flex justify-center mb-10 animate-fade-in-slow">
                    <BrandMark size={72} ceremonial />
                </div>

                <p className="label-eyebrow text-ink-gold text-center mb-6" data-testid="welcome-eyebrow">
                    {t("welcome.eyebrow")}
                </p>
                <h1
                    className="h-serif text-center text-4xl md:text-5xl lg:text-6xl leading-[1.08] max-w-2xl mx-auto"
                    data-testid="welcome-title"
                >
                    {t("welcome.title")}
                </h1>
                <p
                    className="mt-8 text-center text-ink-textMid text-lg md:text-xl leading-relaxed max-w-xl mx-auto"
                    data-testid="welcome-body"
                >
                    {t("welcome.body")}
                </p>

                {/* Visual steps — always visible. No hidden state, no confusion. */}
                <div
                    className="mt-16 md:mt-20 grid md:grid-cols-3 gap-5 md:gap-6"
                    data-testid="welcome-steps"
                >
                    {steps.map((s) => {
                        const Icon = s.icon;
                        return (
                            <div
                                key={s.n}
                                className="border border-ink-border bg-ink-surface/50 rounded-sm p-7 md:p-8 flex flex-col items-start gap-4 transition-colors hover:border-ink-gold/60"
                                data-testid={`welcome-step-${s.n}`}
                            >
                                <div className="flex items-center gap-3">
                                    <Icon className="text-ink-gold" size={22} />
                                    <span className="label-eyebrow text-ink-textLow">{s.n}</span>
                                </div>
                                <p className="font-serif text-ink-textHigh text-[1.35rem] leading-snug">
                                    {s.label}
                                </p>
                            </div>
                        );
                    })}
                </div>

                {/* CTA — single, clear, action-oriented */}
                <div className="mt-16 flex flex-col items-center gap-4">
                    <button
                        type="button"
                        onClick={() => nav("/presence/new")}
                        className="btn-primary-glow inline-flex items-center gap-2"
                        data-testid="welcome-cta"
                    >
                        {t("welcome.cta")}
                        <ArrowRight size={18} />
                    </button>
                    <button
                        type="button"
                        onClick={() => nav("/dashboard")}
                        className="text-sm text-ink-textLow hover:text-ink-textMid transition-colors tracking-wide"
                        data-testid="welcome-skip"
                    >
                        {t("welcome.skip")}
                    </button>
                </div>
            </main>
        </div>
    );
}
